// The brief: words an agent writes for the board, like a morning brief or a
// weekly report. The board does not write it. An agent does, when asked (the
// button here asks whoever builds for this board: the chat beside it, or the
// user's own agent), or on its own schedule, by sending text to the widget's
// stream. Laid out like a good newsletter: who wrote it and when, a headline,
// short sections, and two columns when the tile is wide enough for them.

import { AlarmClock, PenLine } from "lucide-react";
import { cleanAuto } from "../../shared/brief";
import { slugifyKey } from "../../shared/ids";
import type { WidgetItem } from "../../shared/types";
import { briefAsk } from "../agent/asks";
import { askWho, handOff, useBuilder } from "../agent/handoff";
import { useSize } from "../charts/charts";
import { cx } from "../lib/util";
import { updateItem } from "../state/actions";
import { get, set, useStore } from "../state/store";
import type { WP } from "./data";
import { EmptyData } from "./empty";
import { Markdown } from "./markdown";
import { useTick } from "./tick";
import { useJobRunning } from "./job";
import { DAY } from "../../shared/period";

/** "7:02 am", "Yesterday, 9:14 pm", "Thursday, 8:30 am" or "Oct 2", for when the brief was written. */
export function writtenWhen(t: number, now = Date.now()): string {
  const d = new Date(t);
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }).toLowerCase();
  if (t >= start.getTime()) return time;
  if (t >= start.getTime() - DAY) return `Yesterday, ${time}`;
  if (t >= start.getTime() - 6 * DAY) return `${d.toLocaleDateString("en-US", { weekday: "long" })}, ${time}`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** The words without a first heading that only repeats the tile's title, which the tile already shows. */
export function withoutTitle(text: string, title: string): string {
  const m = /^\s*#{1,3}\s+(.+?)\s*#*\s*(\n|$)/.exec(text);
  return m && title.trim() && m[1].trim().toLowerCase() === title.trim().toLowerCase() ? text.slice(m[0].length) : text;
}

/** The stream a brief is written to: its own, or one made for it the first time it is asked for. */
function briefStream(item: WidgetItem): string {
  if (item.stream) return item.stream;
  const taken = new Set(Object.keys(get().streams));
  let key = slugifyKey(item.title || "brief") || "brief";
  for (let n = 2; taken.has(key); n++) key = `${slugifyKey(item.title || "brief") || "brief"}-${n}`;
  updateItem(item.id, { stream: key } as never, "Give the brief a stream");
  return key;
}

function ask(item: WidgetItem) {
  handOff(briefAsk(item, briefStream(item)));
}

export function Brief({ item, stream, readOnly }: WP) {
  useTick(60_000);
  const auto = cleanAuto(item.props.auto);
  const online = useStore((s) => !!s.runner?.online);
  const builder = useBuilder();
  const [ref, size] = useSize<HTMLDivElement>();
  const text = stream?.text ?? (typeof stream?.value === "string" ? stream.value : "");
  // reading size, not poster size: the words grow a little with the tile, then the tile shows more of them
  const px = size.w ? Math.min(16.5, Math.max(12.5, size.w / 27)) : 13.5;
  const cols = size.w >= 52 * px && size.h >= 14 * px ? 2 : 1;
  // being written now on the user's computer: it says so until it is in
  const writing = useJobRunning(item.id) > 0 && !readOnly;
  if (!text.trim() && writing)
    return (
      <div className="br br-empty" ref={ref}>
        <span className="spinner" />
        <b>Writing today's brief</b>
        <span>Your agent is on it. It shows here when it is done.</span>
      </div>
    );
  // a source that writes it and failed before it ever did: the reason, and the way to fix it
  if (!text.trim() && stream?.error) return <EmptyData item={item} stream={stream} readOnly={readOnly} />;
  if (!text.trim())
    return (
      <div className="br br-empty" ref={ref}>
        <span className="br-empty-ico">
          <PenLine />
        </span>
        <b>{readOnly ? "Nothing written yet" : "Your morning brief"}</b>
        {!readOnly && (
          <>
            <span>Your agent reads your board and writes a short brief: the one thing that matters today, what changed, what needs you, and the three things to do first.</span>
            <div className="br-empty-acts">
              <button className="btn btn-sm btn-primary" onClick={() => ask(item)} data-interactive>
                <PenLine size={13} /> Write today's brief
              </button>
              {!auto?.on && (
                <button className="btn btn-sm" onClick={() => set({ selection: [item.id], inspect: true, editing: null })} title="Have your computer write it each morning" data-interactive>
                  <AlarmClock size={13} /> Every morning
                </button>
              )}
            </div>
            {auto?.on && (
              <span className="br-empty-note">
                Your computer writes the next one at {auto.time}
                {online ? "" : ", once it is on"}.{" "}
                <button className="link-btn" onClick={() => set({ selection: [item.id], inspect: true, editing: null })} data-interactive>
                  Change it
                </button>
              </span>
            )}
          </>
        )}
      </div>
    );
  const at = stream?.updatedAt ?? 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const old = at > 0 && at < today.getTime();
  return (
    <div className={cx("br", cols === 2 && "is-cols")} ref={ref} style={{ fontSize: px }} data-scroll>
      <div className="br-meta">
        {writing ? (
          <span className="pw-running">
            <span className="spinner" /> Writing a new one now
          </span>
        ) : (
          <span className={cx((old || stream?.error) && "is-old")} title={stream?.error ? `Could not refresh: ${stream.error}` : undefined}>
            {at > 0 ? `Written ${writtenWhen(at)}` : "Written"}
            {stream?.updatedBy ? ` by ${stream.updatedBy}` : ""}
            {stream?.error ? ". Could not refresh it since" : ""}
          </span>
        )}
        {!readOnly && (
          <button className={cx("br-again", old && "is-old")} onClick={() => ask(item)} title={`Ask ${askWho(builder)} for a new one`} data-interactive>
            <PenLine /> {old ? "Write today's" : "Write a new one"}
          </button>
        )}
      </div>
      <Markdown text={withoutTitle(text, item.title)} className="md em br-md" />
    </div>
  );
}
